import type {
  ConditionGroupV3Operator,
  RuleV3Condition,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import type { RuleTriggerType } from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useCallback } from "react";
import type { ExecutionData } from "./condition-group-editor-v3";
import { ConditionItemV3 } from "./condition-item-v3";

export interface ConditionListV3Props {
  conditions: RuleV3Condition[];
  onChange: (conditions: RuleV3Condition[]) => void;
  operator: ConditionGroupV3Operator;
  triggerType?: RuleTriggerType;
  executionData?: ExecutionData;
  readonly?: boolean;
}

export const ConditionListV3 = memo(function ConditionListV3({
  conditions,
  onChange,
  operator,
  triggerType,
  executionData,
  readonly,
}: ConditionListV3Props) {
  const handleConditionChange = useCallback(
    (index: number, newCondition: RuleV3Condition) => {
      const newConditions = [...conditions];
      newConditions[index] = newCondition;
      onChange(newConditions);
    },
    [conditions, onChange],
  );

  const handleConditionDelete = useCallback(
    (index: number) => {
      onChange(conditions.filter((_, i) => i !== index));
    },
    [conditions, onChange],
  );

  const badgeColor = operator === "AND" ? "blue" : "warning";
  const badgeText = operator === "AND" ? "And" : "Or";

  return (
    <Flex dir="column" gap="none">
      {conditions.map((condition, index) => (
        <Flex dir="column" gap="none" key={index}>
          {index > 0 && (
            <Flex align="flex-start" dir="column" gap="none">
              <Flex align="center" dir="column" gap="sm">
                <div
                  style={{
                    width: "1px",
                    height: "16px",
                    backgroundColor: "var(--redo-colors-gray-modern-200)",
                  }}
                />
                <RedoBadge color={badgeColor} size="sm" text={badgeText} />
                <div
                  style={{
                    width: "1px",
                    height: "16px",
                    backgroundColor: "var(--redo-colors-gray-modern-200)",
                  }}
                />
              </Flex>
            </Flex>
          )}

          <ConditionItemV3
            condition={condition}
            executionResult={executionData?.results.find(
              (r) => r.conditionType === condition.conditionType,
            )}
            onChange={(newCondition) =>
              handleConditionChange(index, newCondition)
            }
            onDelete={() => handleConditionDelete(index)}
            readonly={readonly}
            triggerType={triggerType}
            valuesAtExecution={executionData?.valuesAtExecution}
          />
        </Flex>
      ))}
    </Flex>
  );
});
